import React, { useCallback, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CloudUpload, Storage } from '@mui/icons-material';
import { customFetcher } from '../../hooks/useFetch';
import { useAlert } from '../../context/AlertContext';
import { useFederalStates } from '../../Aemter/hooks';

type SeedMode = 'migration' | 'both';

interface ISeedResult {
  catalog: string;
  migrationFile: string;
  statements: number;
  summary: Record<string, number>;
  applied: boolean;
  applyError?: string | null;
}

const MODE_LABEL: Record<SeedMode, string> = {
  migration: 'Nur Migration erzeugen',
  both: 'Migration erzeugen + sofort auf diese Instanz anwenden',
};

/**
 * /admin/dev/seed — erzeugt aus einem Katalog-JSON (Amtsgerichte-Liste oder
 * Grundbuch-Gemarkungen) eine idempotente, id-unabhängige Seed-Migration in
 * backend-private/migration/. Diese wird in Dev geprüft, committet und rollt
 * dann bei allen Instanzen aus. Ersetzt die manuellen Import-Skripte.
 */
export const SeedImportOverview: React.FC = () => {
  const { showAlert } = useAlert();
  const { federalStates } = useFederalStates();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [mode, setMode] = useState<SeedMode>('migration');
  const [idFederalState, setIdFederalState] = useState<number | ''>('');
  const [fileName, setFileName] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ISeedResult | null>(null);

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const res = String(reader.result);
        const idx = res.indexOf(',');
        resolve(idx >= 0 ? res.slice(idx + 1) : res);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFile = useCallback(
    async (file: File) => {
      setFileName(file.name);
      setResult(null);
      setBusy(true);
      try {
        const dataB64 = await fileToBase64(file);
        const body: Record<string, unknown> = {
          FileName: file.name,
          DataBase64: dataB64,
          Mode: mode,
        };
        if (typeof idFederalState === 'number') {
          body.idFederalState = idFederalState;
        }
        const res: ISeedResult = await customFetcher({
          endpoint: '/admin/seed/import',
          method: 'POST',
          body,
        });
        setResult(res);
        showAlert({
          severity: 'success',
          text: `Migration erzeugt (${res.catalog}): ${res.statements} Statements.${
            res.applied ? ' Auf diese Instanz angewendet.' : ''
          }`,
        });
      } catch (e) {
        // customFetcher wirft ApiError mit body.fehler
        const msg =
          e &&
          typeof e === 'object' &&
          'body' in e &&
          (e as { body?: { fehler?: string } }).body?.fehler
            ? (e as { body?: { fehler?: string } }).body!.fehler!
            : 'Import fehlgeschlagen.';
        showAlert({ severity: 'error', text: msg });
      } finally {
        setBusy(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [mode, idFederalState, showAlert]
  );

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1 }}>
        <Storage />
        <Typography variant='h5'>Stammdaten-Seed / Katalog-Import</Typography>
      </Stack>

      <Alert severity='warning' sx={{ mb: 2 }}>
        Versteckter Admin-Bereich. Aus dem hochgeladenen Katalog-JSON wird eine
        <b> datierte SQL-Migration</b> erzeugt (idempotent, id-unabhängig per
        Namens-Matching). Diese in Dev prüfen/ausführen, committen — dann rollt
        sie bei allen Instanzen beim nächsten Deployment aus.
      </Alert>

      <Alert severity='info' sx={{ mb: 3 }}>
        <AlertTitle>Unterstützte Kataloge (Auto-Erkennung)</AlertTitle>
        <b>Amtsgerichte</b> (Liste mit <code>gericht</code>/
        <code>bundesland</code>) → Gerichte je Bundesland. ·{' '}
        <b>Grundbuch-Gemarkungen</b> (Dict mit <code>marking_array</code>) →
        Gemarkungen + Grundbuchamt-Zuordnung; hierfür{' '}
        <b>Ziel-Bundesland wählen</b>. Reihenfolge: erst Amtsgerichte, dann
        Gemarkungen (die Zuordnung matcht Gerichte über den Namen).
      </Alert>

      <Paper variant='outlined' sx={{ p: 2 }}>
        <Stack spacing={2}>
          <TextField
            select
            label='Modus'
            value={mode}
            onChange={e => setMode(e.target.value as SeedMode)}
            sx={{ maxWidth: 520 }}
          >
            {(['migration', 'both'] as SeedMode[]).map(m => (
              <MenuItem key={m} value={m}>
                {MODE_LABEL[m]}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label='Ziel-Bundesland (nur für Grundbuch-Gemarkungen)'
            value={idFederalState === '' ? '' : String(idFederalState)}
            onChange={e =>
              setIdFederalState(
                e.target.value === '' ? '' : Number(e.target.value)
              )
            }
            sx={{ maxWidth: 520 }}
          >
            <MenuItem value=''>—</MenuItem>
            {(federalStates ?? [])
              .slice()
              .sort((a, b) => a.FederalState.localeCompare(b.FederalState))
              .map(fs => (
                <MenuItem key={fs.idFederalState} value={fs.idFederalState}>
                  {fs.FederalState}
                </MenuItem>
              ))}
          </TextField>

          <Box>
            <input
              ref={fileInputRef}
              type='file'
              accept='.json,application/json'
              style={{ display: 'none' }}
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) void handleFile(f);
              }}
            />
            <Button
              variant='contained'
              startIcon={<CloudUpload />}
              disabled={busy}
              onClick={() => fileInputRef.current?.click()}
            >
              {busy ? 'Verarbeite…' : 'Katalog-JSON wählen'}
            </Button>
            {fileName && (
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ ml: 2 }}
              >
                {fileName}
              </Typography>
            )}
          </Box>
        </Stack>
      </Paper>

      {result && (
        <Paper variant='outlined' sx={{ p: 2, mt: 2 }}>
          <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 1 }}>
            Ergebnis
          </Typography>
          <Stack direction='row' spacing={1} flexWrap='wrap' sx={{ mb: 1 }}>
            <Chip label={`Katalog: ${result.catalog}`} size='small' />
            <Chip label={`${result.statements} Statements`} size='small' />
            {Object.entries(result.summary).map(([k, v]) => (
              <Chip key={k} label={`${k}: ${v}`} size='small' color='primary' />
            ))}
            <Chip
              label={result.applied ? 'angewendet' : 'nur Datei'}
              size='small'
              color={result.applied ? 'success' : 'default'}
            />
          </Stack>
          <Typography variant='body2'>
            Migrationsdatei: <code>{result.migrationFile}</code>
          </Typography>
          {result.applyError && (
            <Alert severity='error' sx={{ mt: 1 }}>
              Anwenden fehlgeschlagen: {result.applyError}
            </Alert>
          )}
          <Alert severity='info' sx={{ mt: 1 }}>
            Datei prüfen, in Dev ausführen (falls nicht „both") und anschließend
            committen, damit alle Instanzen sie ausrollen.
          </Alert>
        </Paper>
      )}
    </Box>
  );
};
